// Combat event playback engine.
//
// Reads CombatEvent[] from the deterministic simulator and animates the
// PIXI stage to match. Each event becomes one or more tween/particle calls,
// sequenced via `await`. The Skip signal aborts in-flight tweens (they
// snap to their end-state) and lets the loop drop through remaining events
// without animating — so the user always ends up at the final board state
// rendered correctly.
//
// The engine never mutates real game state — it mutates display state
// (CombatUnit.setHp etc.) only. Damage application against `match.health`
// happens later in `applyCombatDamage()`.

import { Container, Graphics, Text, TextStyle } from 'pixi.js';
import type { CombatEvent, CombatResult } from '../game/combatEngine';
import type { Card } from '../game/data/cards/types';
import { CombatUnit, UNIT_H, UNIT_W } from './CombatUnit';
import { Ease, scaledDuration, tween, wait } from './animations';
import { emitBurst } from './particles';
import { getTribeDefinition } from '../game/data/tribes';
import { sfx } from '../audio/sfx';
import { haptics } from '../audio/haptics';

const COLOR_AEROSOL_RED = 0xe6342a;
const COLOR_GLOW_STICK = 0x5fd17e;
const COLOR_BONE = 0xf5ecd6;
const COLOR_SODIUM = 0xff8a3d;
const COLOR_SHIELD_BLUE = 0x9ec8d8;
const COLOR_DIVINE_GOLD = 0xffd97a;
const COLOR_CHOLO = 0xff5a1f;
const COLOR_EMO = 0xff2e7e;

interface PlaybackLayers {
  /** Stage root — used for screen-shake offset. */
  stage: Container;
  /** Units layer — board sprites live here. */
  units: Container;
  /** FX layer above units — particles, damage popups. */
  fx: Container;
  /** Screen-wide flash overlay. */
  flash: Graphics;
}

export interface PlaybackContext {
  layers: PlaybackLayers;
  /** Resolve a unit by instanceId. Returns undefined if it's been removed. */
  getUnit: (id: string) => CombatUnit | undefined;
  /** Register a newly summoned unit. */
  addUnit: (unit: CombatUnit, owner: 'player' | 'enemy', position: number) => void;
  /** Lay out a board side so positions stay tidy after removals/summons. */
  relayout: (owner: 'player' | 'enemy') => void;
  /** Current playback speed (1, 2, 4). */
  getSpeed: () => number;
  /** Abort signal — fires when the user hits Skip or the page unmounts. */
  signal: AbortSignal;
  /** Screen width — needed by screen-edge hero_effect FX. */
  width: number;
  height: number;
  /**
   * Ask the camera to frame these world points.
   *
   * Playback declares WHAT matters; the camera decides whether to move
   * (it ignores the call while the player has manual control, at 4x speed,
   * or under reduced motion). Optional so tests can omit it.
   */
  focusCamera?: (points: Array<{ x: number; y: number }>) => void;
}

function tribeOrFactionColor(card: Card): number {
  if (card.tribe) {
    const def = getTribeDefinition(card.tribe);
    if (def) return def.color;
  }
  if (card.faction === 'cholo') return COLOR_CHOLO;
  if (card.faction === 'emo') return COLOR_EMO;
  return COLOR_BONE;
}

// --- individual animation primitives keyed to event types ---

async function animateAttack(
  ctx: PlaybackContext,
  attacker: CombatUnit,
  defender: CombatUnit,
  damage: number,
  counterDamage: number,
): Promise<void> {
  const speed = ctx.getSpeed();

  // Frame the exchange before the wind-up, so the camera is already settling
  // as the lunge starts. Uses the units' HOME positions, not live x/y — the
  // lunge is about to move them, and chasing that would make the camera
  // jitter with every swing.
  ctx.focusCamera?.([
    { x: attacker.homeX, y: attacker.homeY },
    { x: defender.homeX, y: defender.homeY },
  ]);

  // wind-up: pull back slightly opposite the lunge direction
  const direction = defender.x > attacker.x ? 1 : -1;
  const startX = attacker.x;
  const startY = attacker.y;
  const lungeDeltaX = defender.x - attacker.x;
  const lungeDeltaY = defender.y - attacker.y;
  const lungeDistance = Math.hypot(lungeDeltaX, lungeDeltaY);

  // Attack swing — short whoosh at the start of the wind-up so audio leads
  // the visual lunge by ~140ms (feels punchy without dragging).
  sfx.attack();

  // 1) wind-up
  await tween({
    from: 0,
    to: 1,
    durationMs: scaledDuration(140, speed),
    ease: Ease.outQuad,
    signal: ctx.signal,
    onUpdate: (t) => {
      attacker.x = startX - direction * 8 * t;
      attacker.scale.set(1 + 0.05 * t);
    },
  });

  // 2) lunge — arcing path toward defender; stops short of full overlap
  const lungeFraction = Math.min(0.6, 60 / Math.max(1, lungeDistance));
  await tween({
    from: 0,
    to: 1,
    durationMs: scaledDuration(220, speed),
    ease: Ease.inQuad,
    signal: ctx.signal,
    onUpdate: (t) => {
      const arc = Math.sin(t * Math.PI) * 18 * (ctx.layers.units.y === 0 ? 1 : 1);
      attacker.x = startX + lungeDeltaX * lungeFraction * t;
      // Arc lifts toward the opposite side (attacker rises slightly).
      attacker.y = startY + lungeDeltaY * lungeFraction * t - arc;
      attacker.scale.set(1.05 + 0.05 * t);
    },
  });

  // 3) impact — flash + spark burst + camera shake; damage popups
  const impactX = defender.x;
  const impactY = defender.y;

  // Detect killing blows so we can crank the emphasis. A killing blow gets
  // bigger spark counts, harder shake, and a flash-layer pulse — combat
  // deaths should feel like they cost something visually.
  const willKillDefender =
    damage > 0 && !defender.hasDivineShield() && defender.getHp() - damage <= 0;
  const willKillAttacker =
    counterDamage > 0 &&
    !attacker.hasDivineShield() &&
    attacker.getHp() - counterDamage <= 0;
  const lethal = willKillDefender || willKillAttacker;

  // Spark burst at defender — additive yellow/orange. Lethal hits get more.
  emitBurst({
    parent: ctx.layers.fx,
    x: impactX,
    y: impactY,
    kind: 'spark',
    color: COLOR_SODIUM,
    count: willKillDefender ? 70 : 40,
  });
  // Secondary shard sparks tinted to defender's tribe
  emitBurst({
    parent: ctx.layers.fx,
    x: impactX,
    y: impactY,
    kind: 'spark',
    color: tribeOrFactionColor(defender.card),
    count: willKillDefender ? 28 : 16,
    speed: 180,
    life: 320,
  });

  // Camera shake — harder on lethal hits. Operates on the stage container.
  // No screen flash: player feedback for impact comes from particles +
  // shake + damage popups. Full-screen flash is jarring and was removed.
  shakeStage(ctx, scaledDuration(lethal ? 220 : 150, speed), lethal ? 11 : 6);

  // Impact sounds + haptics. Hit popping for damaged side(s); shielded
  // hits get the shield-pop sound via the divine_shield_pop event instead.
  // Haptic level scales with damage so a 2-damage tap and a 9-damage
  // crusher feel different in the hand.
  if (damage > 0 && !defender.hasDivineShield()) {
    sfx.hit(damage);
    haptics.impact(damage);
  }
  if (counterDamage > 0 && !attacker.hasDivineShield()) {
    sfx.hit(counterDamage);
    haptics.impact(counterDamage);
  }

  // Damage popups (run concurrently, don't await). Killing blows render
  // a 'KO' tag in bone color on top of the regular damage number.
  if (damage > 0 && !defender.hasDivineShield()) {
    spawnDamagePopup(
      ctx,
      impactX,
      impactY - 10,
      damage,
      COLOR_AEROSOL_RED,
      undefined,
      willKillDefender ? 'big' : 'normal',
    );
    if (willKillDefender) {
      spawnDamagePopup(ctx, impactX, impactY - 38, 0, COLOR_BONE, 'KO', 'big');
    }
  } else if (defender.hasDivineShield()) {
    spawnDamagePopup(ctx, impactX, impactY - 10, 0, COLOR_DIVINE_GOLD, 'BLOCK');
  }
  if (counterDamage > 0 && !attacker.hasDivineShield()) {
    spawnDamagePopup(
      ctx,
      startX,
      startY - 10,
      counterDamage,
      COLOR_AEROSOL_RED,
      undefined,
      willKillAttacker ? 'big' : 'normal',
    );
    if (willKillAttacker) {
      spawnDamagePopup(ctx, startX, startY - 38, 0, COLOR_BONE, 'KO', 'big');
    }
  }

  // Defender knockback (concurrent)
  const defHome = { x: defender.homeX, y: defender.homeY };
  tween({
    from: 0,
    to: 1,
    durationMs: scaledDuration(160, speed),
    ease: Ease.outBack,
    signal: ctx.signal,
    onUpdate: (t) => {
      defender.x = defHome.x + direction * 10 * Math.sin(t * Math.PI);
      defender.scale.set(1 - 0.05 * Math.sin(t * Math.PI));
    },
  });

  // 4) recoil — attacker springs home
  await tween({
    from: 0,
    to: 1,
    durationMs: scaledDuration(260, speed),
    ease: Ease.outBack,
    signal: ctx.signal,
    onUpdate: (t) => {
      attacker.x = startX + (attacker.homeX - startX) * t + lungeDeltaX * lungeFraction * (1 - t);
      attacker.y = startY + (attacker.homeY - startY) * t;
      attacker.scale.set(1.1 - 0.1 * t);
    },
  });
  attacker.x = attacker.homeX;
  attacker.y = attacker.homeY;
  attacker.scale.set(1);
  defender.x = defender.homeX;
  defender.y = defender.homeY;
  defender.scale.set(1);

  // Update displayed HP on both sides AFTER visuals settle.
  if (damage > 0 && !defender.hasDivineShield()) {
    defender.setHp(defender.getHp() - damage);
  }
  if (counterDamage > 0 && !attacker.hasDivineShield()) {
    attacker.setHp(attacker.getHp() - counterDamage);
  }
}

/**
 * Brief white-flash overlay on the dedicated flash Graphics layer.
 * Used for lethal hits — short fade-in + faster fade-out so it pops but
 * doesn't strobe. Concurrent (fire-and-forget); does not await.
 */
function flashScreen(ctx: PlaybackContext, durationMs: number, peakAlpha: number) {
  const flash = ctx.layers.flash;
  tween({
    from: 0,
    to: 1,
    durationMs,
    ease: Ease.pulseSine,
    signal: ctx.signal,
    onUpdate: (v) => {
      flash.alpha = v * peakAlpha;
    },
  }).then(() => {
    flash.alpha = 0;
  });
}

function shakeStage(ctx: PlaybackContext, durationMs: number, amplitude: number) {
  const stage = ctx.layers.stage;
  const baseX = 0;
  const baseY = 0;
  tween({
    from: 0,
    to: 1,
    durationMs,
    ease: Ease.outQuad,
    signal: ctx.signal,
    onUpdate: (t) => {
      const decay = 1 - t;
      stage.x = baseX + (Math.random() - 0.5) * amplitude * decay;
      stage.y = baseY + (Math.random() - 0.5) * amplitude * decay;
    },
  }).then(() => {
    stage.x = baseX;
    stage.y = baseY;
  });
}

function spawnDamagePopup(
  ctx: PlaybackContext,
  x: number,
  y: number,
  amount: number,
  color: number,
  overrideText?: string,
  /** Visual emphasis tier — 'big' bumps the font + extends rise + lingers. */
  emphasis: 'normal' | 'big' = 'normal',
) {
  const speed = ctx.getSpeed();
  // Scale font with damage magnitude so big hits read big. 1–7 → base 22,
  // 8–14 → 28, 15+ → 34. Caller can also force 'big' (used for killing blows).
  const autoBig = amount >= 8;
  const isBig = emphasis === 'big' || autoBig;
  const fontSize =
    emphasis === 'big' ? 34 : amount >= 15 ? 34 : amount >= 8 ? 28 : 22;
  const text = new Text({
    text: overrideText ?? `-${amount}`,
    style: new TextStyle({
      fontFamily: 'JetBrains Mono, Consolas, monospace',
      fontSize,
      fontWeight: 'bold',
      fill: color,
      stroke: { color: 0x000000, width: isBig ? 4 : 3 },
    }),
  });
  text.anchor.set(0.5);
  text.x = x;
  text.y = y;
  ctx.layers.fx.addChild(text);

  // Big popups travel farther and linger slightly longer.
  const riseDist = isBig ? 46 : 32;
  const durationMs = scaledDuration(isBig ? 900 : 700, speed);

  tween({
    from: 0,
    to: 1,
    durationMs,
    ease: Ease.outCubic,
    signal: ctx.signal,
    onUpdate: (t) => {
      text.y = y - riseDist * t;
      text.alpha = 1 - t * t;
      // Big popups punch in then settle to base scale
      if (isBig) {
        const punch = t < 0.15 ? 1 + (0.15 - t) * 1.4 : 1;
        text.scale.set(punch);
      }
    },
  }).then(() => {
    text.destroy();
  });
}

async function animateDeath(
  ctx: PlaybackContext,
  unit: CombatUnit,
  owner: 'player' | 'enemy',
): Promise<void> {
  if (unit.isDead()) return;
  unit.markDead();
  const speed = ctx.getSpeed();
  const color = tribeOrFactionColor(unit.card);

  // Faction-tinted death sound + medium haptic. Death is always
  // weightier than a basic hit — the haptic underscores the finality.
  const faction = unit.card.faction;
  sfx.death(faction === 'cholo' || faction === 'emo' ? faction : 'neutral');
  haptics.impact(5);

  // Paint-drip burst at the unit's position
  emitBurst({
    parent: ctx.layers.fx,
    x: unit.x,
    y: unit.y + 10,
    kind: 'drip',
    color,
    count: 16,
  });
  emitBurst({
    parent: ctx.layers.fx,
    x: unit.x,
    y: unit.y,
    kind: 'spark',
    color,
    count: 18,
    speed: 160,
    life: 320,
  });

  await tween({
    from: 1,
    to: 0,
    durationMs: scaledDuration(320, speed),
    ease: Ease.inQuad,
    signal: ctx.signal,
    onUpdate: (v, t) => {
      unit.setFadeAlpha(v);
      unit.scale.set(1 - 0.4 * t);
    },
  });

  unit.visible = false;
  ctx.relayout(owner);
}

async function animateBuff(
  ctx: PlaybackContext,
  unit: CombatUnit,
  atkDelta: number,
  hpDelta: number,
): Promise<void> {
  const speed = ctx.getSpeed();
  unit.setAtk(unit.getAtk() + atkDelta);
  unit.setHp(unit.getHp() + hpDelta);
  unit.glowBadges();

  // Floating +X/+Y popup in sodium
  const parts: string[] = [];
  if (atkDelta) parts.push(`${atkDelta >= 0 ? '+' : ''}${atkDelta}`);
  if (hpDelta) parts.push(`${hpDelta >= 0 ? '+' : ''}${hpDelta}`);
  if (parts.length) {
    const popup = new Text({
      text: parts.join(' / '),
      style: new TextStyle({
        fontFamily: 'JetBrains Mono, Consolas, monospace',
        fontSize: 16,
        fontWeight: 'bold',
        fill: COLOR_SODIUM,
        stroke: { color: 0x000000, width: 3 },
      }),
    });
    popup.anchor.set(0.5);
    popup.x = unit.x;
    popup.y = unit.y - 10;
    ctx.layers.fx.addChild(popup);
    tween({
      from: 0,
      to: 1,
      durationMs: scaledDuration(700, speed),
      ease: Ease.outCubic,
      signal: ctx.signal,
      onUpdate: (t) => {
        popup.y = unit.y - 10 - 26 * t;
        popup.alpha = 1 - t * t;
      },
    }).then(() => popup.destroy());
  }

  // Pulse the unit
  await tween({
    from: 0,
    to: 1,
    durationMs: scaledDuration(260, speed),
    ease: Ease.pulseSine,
    signal: ctx.signal,
    onUpdate: (v) => {
      unit.scale.set(1 + 0.18 * v);
    },
  });
  unit.scale.set(1);
}

async function animateSummon(
  ctx: PlaybackContext,
  card: Card,
  owner: 'player' | 'enemy',
  position: number,
): Promise<void> {
  const speed = ctx.getSpeed();
  // The CombatPage knows board layout; we receive raw position and ask the
  // ctx to add this unit at the next free home slot for the owner.
  // The unit is created here so animation runs are local to this call.
  const sprite = new CombatUnit(card, owner, 0, 0);
  ctx.addUnit(sprite, owner, position);
  sprite.scale.set(0.2);
  sprite.alpha = 0;

  // Ring burst
  emitBurst({
    parent: ctx.layers.fx,
    x: sprite.x,
    y: sprite.y,
    kind: 'ring',
    color: COLOR_SODIUM,
    count: 4,
  });

  await tween({
    from: 0,
    to: 1,
    durationMs: scaledDuration(360, speed),
    ease: Ease.outBack,
    signal: ctx.signal,
    onUpdate: (v, t) => {
      sprite.scale.set(0.2 + 0.8 * v);
      sprite.alpha = t;
    },
  });
  sprite.scale.set(1);
  sprite.alpha = 1;
}

async function animateDivineShieldPop(
  ctx: PlaybackContext,
  unit: CombatUnit,
): Promise<void> {
  const speed = ctx.getSpeed();
  unit.setShield(false);

  emitBurst({
    parent: ctx.layers.fx,
    x: unit.x,
    y: unit.y,
    kind: 'shard',
    color: COLOR_SHIELD_BLUE,
    count: 20,
  });
  emitBurst({
    parent: ctx.layers.fx,
    x: unit.x,
    y: unit.y,
    kind: 'ring',
    color: COLOR_DIVINE_GOLD,
    count: 3,
  });

  await wait(scaledDuration(160, speed), ctx.signal);
}

async function animatePoisonKill(
  ctx: PlaybackContext,
  target: CombatUnit,
): Promise<void> {
  const speed = ctx.getSpeed();
  emitBurst({
    parent: ctx.layers.fx,
    x: target.x,
    y: target.y,
    kind: 'drip',
    color: 0x5fd17e,
    count: 18,
  });
  target.setHp(0);
  await wait(scaledDuration(120, speed), ctx.signal);
}

async function animateReborn(
  ctx: PlaybackContext,
  unit: CombatUnit,
): Promise<void> {
  const speed = ctx.getSpeed();
  unit.visible = true;
  unit.setHp(1);
  unit.alpha = 0;
  unit.scale.set(0.3);
  emitBurst({
    parent: ctx.layers.fx,
    x: unit.x,
    y: unit.y,
    kind: 'ring',
    color: COLOR_GLOW_STICK,
    count: 4,
  });
  await tween({
    from: 0,
    to: 1,
    durationMs: scaledDuration(320, speed),
    ease: Ease.outBack,
    signal: ctx.signal,
    onUpdate: (v, t) => {
      unit.alpha = t;
      unit.scale.set(0.3 + 0.7 * v);
    },
  });
  unit.scale.set(1);
  unit.alpha = 1;
}

async function animateCleave(
  ctx: PlaybackContext,
  target: CombatUnit,
  damage: number,
): Promise<void> {
  const speed = ctx.getSpeed();
  if (damage > 0 && !target.hasDivineShield()) {
    target.setHp(target.getHp() - damage);
  }
  emitBurst({
    parent: ctx.layers.fx,
    x: target.x,
    y: target.y,
    kind: 'spark',
    color: COLOR_AEROSOL_RED,
    count: 18,
  });
  spawnDamagePopup(ctx, target.x, target.y - 10, damage, COLOR_AEROSOL_RED);
  await wait(scaledDuration(120, speed), ctx.signal);
}

async function animateHeroEffect(ctx: PlaybackContext): Promise<void> {
  const speed = ctx.getSpeed();
  // Localized sodium ring burst at the canvas center — no full-screen flash.
  emitBurst({
    parent: ctx.layers.fx,
    x: ctx.width / 2,
    y: ctx.height / 2,
    kind: 'ring',
    color: COLOR_SODIUM,
    count: 5,
  });
  await wait(scaledDuration(200, speed), ctx.signal);
}

async function animateHeroHeal(ctx: PlaybackContext, amount: number): Promise<void> {
  const speed = ctx.getSpeed();
  const popup = new Text({
    text: `+${amount} HP`,
    style: new TextStyle({
      fontFamily: 'JetBrains Mono, Consolas, monospace',
      fontSize: 20,
      fontWeight: 'bold',
      fill: COLOR_GLOW_STICK,
      stroke: { color: 0x000000, width: 3 },
    }),
  });
  popup.anchor.set(0.5);
  popup.x = ctx.width / 2;
  popup.y = ctx.height - 40;
  ctx.layers.fx.addChild(popup);
  await tween({
    from: 0,
    to: 1,
    durationMs: scaledDuration(900, speed),
    ease: Ease.outCubic,
    signal: ctx.signal,
    onUpdate: (t) => {
      popup.y = ctx.height - 40 - 40 * t;
      popup.alpha = 1 - t * t;
    },
  });
  popup.destroy();
}

// --- intro / outro choreography ---

/**
 * Brief pre-combat intro: a single sodium ring burst + "FIGHT" text that
 * fades in and out at canvas center. Sets anticipation before the first
 * attack. Abort-safe; resolves immediately if signal fires.
 */
export async function playIntro(ctx: PlaybackContext): Promise<void> {
  const speed = ctx.getSpeed();
  if (ctx.signal.aborted) return;

  const cx = ctx.width / 2;
  const cy = ctx.height / 2;

  // Sodium ring burst at center — same vocabulary as hero_effect so it
  // reads as game-language, not a one-off intro flourish.
  emitBurst({
    parent: ctx.layers.fx,
    x: cx,
    y: cy,
    kind: 'ring',
    color: COLOR_SODIUM,
    count: 3,
  });

  const text = new Text({
    text: 'FIGHT',
    style: new TextStyle({
      fontFamily: 'JetBrains Mono, Consolas, monospace',
      fontSize: 56,
      fontWeight: 'bold',
      fill: COLOR_SODIUM,
      stroke: { color: 0x000000, width: 5 },
      letterSpacing: 8,
    }),
  });
  text.anchor.set(0.5);
  text.x = cx;
  text.y = cy;
  text.alpha = 0;
  text.scale.set(0.6);
  ctx.layers.fx.addChild(text);

  // Punch in + linger + fade out, in one tween cycle.
  await tween({
    from: 0,
    to: 1,
    durationMs: scaledDuration(700, speed),
    ease: Ease.outCubic,
    signal: ctx.signal,
    onUpdate: (_, t) => {
      // 0–0.25 fade in + scale up. 0.25–0.65 hold. 0.65–1 fade out.
      if (t < 0.25) {
        const k = t / 0.25;
        text.alpha = k;
        text.scale.set(0.6 + 0.4 * k);
      } else if (t < 0.65) {
        text.alpha = 1;
        text.scale.set(1);
      } else {
        const k = (t - 0.65) / 0.35;
        text.alpha = 1 - k;
        text.scale.set(1 + 0.15 * k);
      }
    },
  });
  text.destroy();
}

// --- top-level dispatcher ---

export async function playEvents(
  result: CombatResult,
  ctx: PlaybackContext,
): Promise<void> {
  const speed = () => ctx.getSpeed();
  for (const ev of result.events) {
    if (ctx.signal.aborted) return;
    await dispatch(ev, ctx);
    if (ctx.signal.aborted) return;
    await wait(scaledDuration(80, speed()), ctx.signal);
  }
}

async function dispatch(ev: CombatEvent, ctx: PlaybackContext): Promise<void> {
  switch (ev.type) {
    case 'attack': {
      const a = ctx.getUnit(ev.attackerId);
      const d = ctx.getUnit(ev.defenderId);
      if (a && d) {
        await animateAttack(ctx, a, d, ev.damage, ev.counterDamage ?? 0);
      }
      return;
    }
    case 'death': {
      const u = ctx.getUnit(ev.unitId);
      if (u && ev.owner) {
        await animateDeath(ctx, u, ev.owner);
      }
      return;
    }
    case 'ability_buff': {
      const u = ctx.getUnit(ev.targetId);
      if (u) await animateBuff(ctx, u, ev.atk, ev.hp);
      return;
    }
    case 'permanent_buff': {
      const u = ctx.getUnit(ev.unitId);
      if (u) await animateBuff(ctx, u, ev.atk, ev.hp);
      return;
    }
    case 'ability_summon': {
      await animateSummon(ctx, ev.card, ev.owner, ev.position);
      return;
    }
    case 'divine_shield_pop': {
      const u = ctx.getUnit(ev.unitId);
      if (u) await animateDivineShieldPop(ctx, u);
      return;
    }
    case 'poisonous_kill': {
      const u = ctx.getUnit(ev.targetId);
      if (u) await animatePoisonKill(ctx, u);
      return;
    }
    case 'reborn': {
      const u = ctx.getUnit(ev.unitId);
      if (u) await animateReborn(ctx, u);
      return;
    }
    case 'cleave_damage': {
      const u = ctx.getUnit(ev.targetId);
      if (u) await animateCleave(ctx, u, ev.damage);
      return;
    }
    case 'hero_effect': {
      await animateHeroEffect(ctx);
      return;
    }
    case 'hero_heal': {
      await animateHeroHeal(ctx, ev.amount);
      return;
    }
    case 'avenge_trigger': {
      // Subtle pulse on the unit; no buff (the engine emits ability_buff after)
      const u = ctx.getUnit(ev.unitId);
      if (u) {
        emitBurst({
          parent: ctx.layers.fx,
          x: u.x,
          y: u.y,
          kind: 'ring',
          color: COLOR_SODIUM,
          count: 3,
        });
        await wait(scaledDuration(80, ctx.getSpeed()), ctx.signal);
      }
      return;
    }
    default:
      return;
  }
}

// Re-export for CombatCanvas consumer
export { UNIT_W, UNIT_H };
